/** Load (and keep listening) all reportInfo entries for a given user from the DB */
import 'firebase/compat/firestore';
import { myFirebase } from '@/firebaseConfig/firebaseConfig';
import { collection, orderBy, query, where } from 'firebase/firestore';
import { useMemo, useState } from 'react';
import { useCollectionData } from 'react-firebase-hooks/firestore';

import {
  ReportId,
  ReportInfo,
  ReportInfoDict,
  ReportInfoId,
  ReportStatus,
  ReportType,
  Session,
  SessionStatus,
  UserId,
  Version,
} from '@/models';

import { useFirebaseTags } from '../useFirebaseTags';

const db = myFirebase.firestore();

type FileInfoId = string;

type FileInfo = {
  id: FileInfoId;
  filename: string;
  tags: string[];
  reportName: string;
  uploadTimestamp: number;
};

type AreasOfConcern = {
  [key: string]: number[];
  col0: number[]; // Left
  col1: number[]; // Right
};

type FileInfoDict = {
  [id in FileInfoId]: FileInfo;
};

type ReportInfoDoc = ReportInfo & {
  id: ReportInfoId;
  reportId: ReportId | undefined;
  fileId: FileInfoId;
  status: ReportStatus;
  errorMessage: string | undefined;
  reportType: ReportType;
  version: Version;
  areasOfConcern: AreasOfConcern;
};

export type UseReportInfoHook = {
  reportInfoDict: ReportInfoDict;
  isLoading: boolean;
  isError: Error | undefined;
  clearError: () => void;
};

export const useReportInfo = (userId: UserId, organizationId: string): UseReportInfoHook => {
  const userRef = db.collection('users').doc(userId);
  const [isError, setIsError] = useState<Error | undefined>();
  const { tags } = useFirebaseTags(organizationId);

  const options = { idField: 'id' };
  const filesRef = userRef.collection('kinematicsFiles');
  const [fileInfoDocs, fileInfosLoading, fileInfosError] = useCollectionData<FileInfo>(filesRef, options);

  const reportInfosRef = userRef.collection('reportInfo');
  const [reportInfoDocs, reportInfosLoading, reportInfosError] = useCollectionData<ReportInfoDoc>(
    reportInfosRef,
    options,
  );
  const sessionsRef = db.collection('sessions').where('therapistId', '==', userId);
  const [sessionDocs, sessionLoading, sessionError] = useCollectionData<Session>(sessionsRef, options);

  const loading = fileInfosLoading || reportInfosLoading || sessionLoading;

  // Called to close the error Toast
  const clearError = () => {
    setIsError(undefined);
  };

  if (fileInfosError) {
    setIsError(fileInfosError);
  } else if (reportInfosError) {
    setIsError(reportInfosError);
  } else if (sessionError) {
    setIsError(sessionError);
  }

  // convert the sessionDocs to a dict of reportId -> sessionId[]
  const reportIdToSessionIds: { [reportId: string]: string[] } = {};
  if (sessionDocs) {
    for (const session of sessionDocs) {
      if (session.reports) {
        for (const reportId of session.reports) {
          if (!reportIdToSessionIds[reportId]) {
            reportIdToSessionIds[reportId] = [];
          }
          reportIdToSessionIds[reportId].push(session.id);
        }
      }
    }
  }

  // console.log('ReportId to SessionIds mapping:', reportIdToSessionIds);

  const convertReportInfos = (): ReportInfoDict => {
    if (!fileInfoDocs || !reportInfoDocs) {
      console.log('No file info or report info docs');
      return {};
    }
    const fileInfoDict: FileInfoDict = fileInfoDocs.reduce((fileInfoDict: FileInfoDict, fileInfo: FileInfo) => {
      fileInfoDict[fileInfo.id] = fileInfo;
      return fileInfoDict;
    }, {});

    return reportInfoDocs.reduce((reportInfoDict: ReportInfoDict, reportInfoDoc: ReportInfoDoc) => {
      const fileInfo = fileInfoDict[reportInfoDoc.fileId];
      const notDeleted = reportInfoDoc.status != ReportStatus.DELETED;
      if (!fileInfo) {
        return reportInfoDict;
      }

      if (!notDeleted) {
        return reportInfoDict;
      }

      // Timestamps in the database are unix timestamps in seconds;
      // Javascript uses unix timestamps in milliseconds
      const uploadTimestamp = new Date(fileInfo.uploadTimestamp * 1000);

      reportInfoDict[reportInfoDoc.id] = {
        ...reportInfoDoc,

        // Initially tags are empty, but will be populated later on
        tags: fileInfo.tags.map((tagId) => {
          if (tags) {
            return (
              tags.find((tag) => tag.id === tagId) ?? {
                id: tagId,
                label: tagId,
                user: null,
              }
            );
          }

          return {
            id: tagId,
            label: tagId,
            user: null,
          };
        }),
        // Is the report in the reportIdToSessionIds mapping?
        isAssigned: reportInfoDoc.id ? reportIdToSessionIds[reportInfoDoc.id]?.length > 0 : false,
        assignedSessionIds: [],
        filename: fileInfo.filename,
        reportName: fileInfo.reportName,
        uploadTimestamp: uploadTimestamp,
      };

      return reportInfoDict;
    }, {});
  };

  const reportInfoDict = useMemo(() => {
    if (!tags) {
      return {};
    }

    return convertReportInfos();
  }, [userId, reportInfoDocs, tags]);

  const hook: UseReportInfoHook = {
    reportInfoDict: reportInfoDict,
    isLoading: loading,
    isError: isError,
    clearError: clearError,
  };

  return useMemo(() => hook, [reportInfoDict, loading, isError]);
};
